// iter 379n — Central Dispatch booked/delivered alert popup.
// Jake: "Ping you the moment a carrier accepts one of your loads so you
// can confirm pickup fast." Instant sonner toast via the `cd.load_booked`
// WS event + a persistent dialog (backed by cd_alerts) so nothing is
// missed while logged out. Delivered sweeps ping here too.
import { useEffect, useState, useCallback } from "react";
import { hasAnyRole } from "../lib/roles";
import axios from "axios";
import { toast } from "sonner";
import { Truck, PackageCheck, Phone, BellRing } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CdBookedAlertPopup = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchPending = useCallback(() => {
    axios.get(`${API}/central-dispatch/alerts/pending`)
      .then((res) => {
        const list = res.data?.items || [];
        setItems(list);
        if (list.length > 0) setOpen(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasAnyRole(["admin", "dispatcher", "manager"])) return;
    fetchPending();
    const onWs = (e) => {
      const evt = e?.detail?.event;
      // iter 397 — Jake: no "Delivered" popup/toast; the status update is enough.
      // iter 399h — Jake: no "Carrier accepted" popup/toast either.
      // iter 400f — Jake: no "Ready to book" popup/toast either (399p reverted).
      if (evt !== "cd.load_canceled" && evt !== "cd.scheduled_post_failed") return;
      const d = e.detail.data || {};
      const who = (d.customer_names || []).filter(Boolean).join(", ");
      if (evt === "cd.scheduled_post_failed") {
        toast.error(`Scheduled CD post failed${who ? ` — ${who}` : ""}`, {
          description: d.error || "Open Central Dispatch to post it manually",
          duration: 15000,
        });
      } else {
        toast.error(`Load ${d.external_id || ""} canceled${who ? ` — ${who}` : ""}`, {
          description: d.carrier_name ? `${d.carrier_name} backed out — cart needs a new ride` : "Cart needs a new ride",
          duration: 15000,
        });
      }
      fetchPending();
    };
    window.addEventListener("ws_event", onWs);
    return () => window.removeEventListener("ws_event", onWs);
  }, [fetchPending]);

  if (!open || items.length === 0) return null;

  const ack = (id) => {
    axios.post(`${API}/central-dispatch/alerts/${id}/ack`).catch(() => {});
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
  };

  const dismissAll = () => {
    items.forEach((it) => axios.post(`${API}/central-dispatch/alerts/${it.id}/ack`).catch(() => {}));
    setItems([]);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-[#111113] border-white/10 text-white" data-testid="cd-alert-popup">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Truck className="h-4 w-4 text-blue-400" />
            Central Dispatch
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.map((it) => (
            <div key={it.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`cd-alert-item-${it.id}`}>
              <div className="flex items-start gap-2">
                {it.type === "load_delivered" ? (
                  <PackageCheck className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                ) : it.type === "funding_ready" ? (
                  <BellRing className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                ) : it.type === "load_canceled" || it.type === "scheduled_post_failed" ? (
                  <Truck className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                ) : (
                  <Truck className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {it.type === "load_delivered" ? "Delivered" : it.type === "funding_ready" ? "Ready to book" : it.type === "load_canceled" ? "Load canceled" : it.type === "scheduled_post_failed" ? "Scheduled post failed" : "Carrier accepted"}
                    {it.external_id ? <span className="text-zinc-400 font-normal ml-1.5">· {it.external_id}</span> : null}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    {(it.customer_names || []).filter(Boolean).join(", ")}
                    {it.destination?.city ? ` → ${it.destination.city}, ${it.destination.state}` : ""}
                  </div>
                  {it.type === "funding_ready" && (
                    <div className="text-xs text-emerald-300/90 mt-1" data-testid={`cd-alert-funding-${it.id}`}>
                      {it.paid_in_full ? "Now Paid in Full" : `Contract now ${it.contract_status || "Waiting on Funding"}`} — payment cleared, go ahead and post this one
                    </div>
                  )}
                  {it.type === "scheduled_post_failed" && (
                    <div className="text-xs text-red-300/90 mt-1" data-testid={`cd-alert-schedfail-${it.id}`}>
                      {it.error || "The scheduled post didn't go out"} — post it manually from Central Dispatch → Post a Load
                    </div>
                  )}
                  {it.type === "load_canceled" && (
                    <div className="text-xs text-red-300/90 mt-1" data-testid={`cd-alert-canceled-${it.id}`}>
                      {it.carrier_name ? `${it.carrier_name} backed out` : "Dispatch canceled"} — this cart needs a new ride
                    </div>
                  )}
                  {it.type !== "load_delivered" && it.type !== "load_canceled" && it.type !== "scheduled_post_failed" && it.type !== "funding_ready" && (
                    <div className="text-xs text-cyan-300/90 mt-1 flex items-center gap-1.5" data-testid={`cd-alert-carrier-${it.id}`}>
                      {it.carrier_name || "Carrier assigned"}
                      {it.carrier_phone ? (
                        <a href={`tel:${it.carrier_phone}`} className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200">
                          <Phone className="h-3 w-3" />{it.carrier_phone}
                        </a>
                      ) : null}
                      <span className="text-zinc-500">— confirm pickup</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs border-white/15 bg-transparent text-zinc-200 hover:bg-white/10"
                  onClick={() => ack(it.id)} data-testid={`cd-alert-ack-${it.id}`}>
                  Got it
                </Button>
              </div>
            </div>
          ))}
        </div>
        {items.length > 1 && (
          <div className="flex justify-end pt-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-400 hover:text-white hover:bg-white/10"
              onClick={dismissAll} data-testid="cd-alert-dismiss-all">
              Dismiss all ({items.length})
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
